import { PayPalApi } from '../util/api';
import { StatusCode } from '../util/enums';
import { storableError } from '../util/errors';
import * as log from '../util/log';

// ================ Action types ================ //

export const ONBOARDING_LINK_REQUEST = 'app/paypal/ONBOARDING_LINK_REQUEST';
export const ONBOARDING_LINK_SUCCESS = 'app/paypal/ONBOARDING_LINK_SUCCESS';
export const ONBOARDING_LINK_ERROR = 'app/paypal/ONBOARDING_LINK_ERROR';

export const MERCHANT_SIGNUP_STATUS_REQUEST = 'app/paypal/MERCHANT_SIGNUP_STATUS_REQUEST';
export const MERCHANT_SIGNUP_STATUS_SUCCESS = 'app/paypal/MERCHANT_SIGNUP_STATUS_SUCCESS';
export const MERCHANT_SIGNUP_STATUS_ERROR = 'app/paypal/MERCHANT_SIGNUP_STATUS_ERROR';

export const CREATE_ORDER_REQUEST = 'app/paypal/CREATE_ORDER_REQUEST';
export const CREATE_ORDER_SUCCESS = 'app/paypal/CREATE_ORDER_SUCCESS';
export const CREATE_ORDER_ERROR = 'app/paypal/CREATE_ORDER_ERROR';

export const CAPTURE_ORDER_REQUEST = 'app/paypal/CAPTURE_ORDER_REQUEST';
export const CAPTURE_ORDER_SUCCESS = 'app/paypal/CAPTURE_ORDER_SUCCESS';
export const CAPTURE_ORDER_ERROR = 'app/paypal/CAPTURE_ORDER_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchOnboardingLinkInProgress: false,
  fetchOnboardingLinkError: null,
  onboardingLink: null,
  fetchPaypalMerchantInProgress: false,
  fetchPaypalMerchantError: null,
  paypalMerchant: null,
  createOrderInProgress: false,
  createOrderError: null,
  paypalOrderData: null,
  captureOrderInProgress: false,
  captureOrderError: null,
  paypalCaptureData: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ONBOARDING_LINK_REQUEST:
      return { ...state, fetchOnboardingLinkError: null, fetchOnboardingLinkInProgress: true };
    case ONBOARDING_LINK_SUCCESS:
      return {
        ...state,
        fetchOnboardingLinkInProgress: false,
        onboardingLink: payload,
      };
    case ONBOARDING_LINK_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchOnboardingLinkError: payload,
        fetchOnboardingLinkInProgress: false,
      };

    case MERCHANT_SIGNUP_STATUS_REQUEST:
      return { ...state, fetchPaypalMerchantError: null, fetchPaypalMerchantInProgress: true };
    case MERCHANT_SIGNUP_STATUS_SUCCESS:
      return {
        ...state,
        fetchPaypalMerchantInProgress: false,
        paypalMerchant: payload,
      };
    case MERCHANT_SIGNUP_STATUS_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchPaypalMerchantError: payload,
        fetchPaypalMerchantInProgress: false,
      };

    case CREATE_ORDER_REQUEST:
      return { ...state, createOrderError: null, createOrderInProgress: true };
    case CREATE_ORDER_SUCCESS:
      return {
        ...state,
        createOrderInProgress: false,
        paypalOrderData: payload,
      };
    case CREATE_ORDER_ERROR:
      console.error(payload);
      return {
        ...state,
        createOrderError: payload,
        createOrderInProgress: false,
      };

    case CAPTURE_ORDER_REQUEST:
      return { ...state, captureOrderError: null, captureOrderInProgress: true };
    case CAPTURE_ORDER_SUCCESS:
      return {
        ...state,
        captureOrderInProgress: false,
        paypalCaptureData: payload,
      };
    case CAPTURE_ORDER_ERROR:
      console.error(payload);
      return {
        ...state,
        captureOrderError: payload,
        captureOrderInProgress: false,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const onboardingLinkRequest = () => ({ type: ONBOARDING_LINK_REQUEST });

export const onboardingLinkSuccess = payload => ({
  type: ONBOARDING_LINK_SUCCESS,
  payload,
});

export const onboardingLinkError = e => ({
  type: ONBOARDING_LINK_ERROR,
  payload: e,
  error: true,
});

export const merchantSignupStatusRequest = () => ({
  type: MERCHANT_SIGNUP_STATUS_REQUEST,
});

export const merchantSignupStatusSuccess = payload => ({
  type: MERCHANT_SIGNUP_STATUS_SUCCESS,
  payload,
});

export const merchantSignupStatusError = e => ({
  type: MERCHANT_SIGNUP_STATUS_ERROR,
  payload: e,
  error: true,
});

export const createOrderRequest = () => ({
  type: CREATE_ORDER_REQUEST,
});

export const createOrderSuccess = payload => ({
  type: CREATE_ORDER_SUCCESS,
  payload,
});

export const createOrderError = e => ({
  type: CREATE_ORDER_ERROR,
  payload: e,
  error: true,
});

export const captureOrderRequest = () => ({
  type: CAPTURE_ORDER_REQUEST,
});

export const captureOrderSuccess = payload => ({
  type: CAPTURE_ORDER_SUCCESS,
  payload,
});

export const captureOrderError = e => ({
  type: CAPTURE_ORDER_ERROR,
  payload: e,
  error: true,
});

// ================ Thunks ================ //

/**
 * Creates a PayPal onboarding link for a seller.
 * @param {Object} params - Parameters for the onboarding link
 * @param {string} params.redirectUrl - The URL that the user will be redirected to after onboarding is complete
 * @returns {Promise<string>} A promise that resolves with the onboarding link if successful, and rejects with an error if not
 */
export const createSellerOnboardingLink = params => async (dispatch, getState, sdk) => {
  try {
    // Dispatch an action to indicate that we are making the request to create the onboarding link
    // This will trigger the loading state in the UI
    dispatch(onboardingLinkRequest());

    // Make the request to the PayPal API to get the onboarding link
    const response = await PayPalApi.getOnboardingLink(params);

    // If the request was successful, and the response contains a link to the onboarding process
    if (response.statusCode === StatusCode.CREATED) {
      // Find the onboarding link in the response
      const onboardingLink = response?.data.links.find(l => l.rel === 'action_url');

      // Dispatch an action to indicate that the onboarding link was successfully created
      // This will trigger the success state in the UI
      dispatch(onboardingLinkSuccess(onboardingLink));

      // Return the onboarding link
      return onboardingLink;
    } else {
      // If the request was not successful, convert the error to a storable error format
      const e = storableError(response);

      // Dispatch an action to indicate that there was an error creating the onboarding link
      // This will trigger the error state in the UI
      dispatch(onboardingLinkError(e));

      // Log the error for tracking/debugging purposes
      log.error(e, 'fetch-paypal-onboarding-links-failed');

      // Throw the error so that the caller can handle it
      throw e;
    }
  } catch (err) {
    // If there was an error, convert it to a storable error format
    const e = storableError(err);

    // Dispatch an action to indicate that there was an error creating the onboarding link
    // This will trigger the error state in the UI
    dispatch(onboardingLinkError(e));

    // Log the error for tracking/debugging purposes
    log.error(e, 'fetch-paypal-onboarding-links-failed');

    // Throw the error so that the caller can handle it
    throw e;
  }
};

/**
 * Fetch the status of the current user's PayPal merchant onboarding process.
 * The request will be sent to the PayPal API.
 * If the request is successful, the response will be dispatched as a success action.
 * If the request fails, an error action will be dispatched.
 * @return {Promise<Object>} The response from the PayPal API
 */
export const fetchPaypalMerchantSignupStatus = () => async dispatch => {
  try {
    dispatch(merchantSignupStatusRequest());

    const response = await PayPalApi.getMerchantOnboardingStatus();
    dispatch(merchantSignupStatusSuccess(response));
    return response;
  } catch (err) {
    const e = storableError(err);
    const status = err?.status || err?.statusCode || err?.response?.status || null;
    const isNonCritical = status === 404 || status === 409;

    dispatch(merchantSignupStatusError(e));

    if (isNonCritical) {
      log.warn(err, 'fetch-paypal-merchant-status-failed-non-critical');
    } else {
      log.error(err, 'fetch-paypal-merchant-status-failed');
    }

    throw e;
  }
};

/**
 * Create a PayPal order, given an object with parameters for the order.
 * @param {object} orderParams - Parameters for the order.
 * @returns {Promise<string>} A promise that resolves with the ID of the created order if successful, and rejects with an error if not.
 */
export const createOrder = orderParams => async (dispatch, getState, sdk) => {
  dispatch(createOrderRequest());

  try {
    const response = await PayPalApi.createOrder(orderParams);
    dispatch(createOrderSuccess(response));
    return response.id;
  } catch (err) {
    const e = storableError(err);
    dispatch(createOrderError(e));
    log.error(err, 'create-paypal-order-failed', e);
    throw e;
  }
};

/**
 * Capture a PayPal order, given an order ID.
 * @param {string} orderId - The ID of the order to capture.
 * @returns {Promise} A promise that resolves with the PayPal response if successful, and rejects with an error if not.
 */
export const captureOrder = orderId => async (dispatch, getState, sdk) => {
  dispatch(captureOrderRequest());

  try {
    const response = await PayPalApi.captureOrder({ orderId });
    dispatch(captureOrderSuccess(response));
    return response;
  } catch (err) {
    const e = storableError(err);
    dispatch(captureOrderError(e));
    log.error(err, 'capture-paypal-order-failed', e);
    throw e;
  }
};

/**
 * Verify PayPal order status by order ID.
 * @param {string} orderId - The PayPal order ID.
 * @returns {Promise<Object>} Verified PayPal order payload.
 */
export const verifyOrder = orderId => async () => {
  try {
    return await PayPalApi.verifyOrder({ orderId });
  } catch (err) {
    const e = storableError(err);
    log.error(err, 'verify-paypal-order-failed', e);
    throw e;
  }
};

/**
 * Capture and complete a PayPal order, given an order ID and the partner fee.
 * @param {string} orderId - The ID of the order to complete.
 * @param {number} partnerFee - The partner fee to apply to the transaction.
 * @returns {Promise} A promise that resolves with the PayPal response if successful, and rejects with an error if not.
 */
export const completeOrder = (orderId, partnerFee) => async (dispatch, getState, sdk) => {
  dispatch(captureOrderRequest());
  try {
    const response = await PayPalApi.completeOrder({ orderId, partnerFee });
    dispatch(captureOrderSuccess(response));
    return response;
  } catch (err) {
    const e = storableError(err);
    dispatch(captureOrderError(e));
    log.error(err, 'capture-paypal-order-failed', e);
    throw e;
  }
};

/**
 * Automatically capture and complete a PayPal order, given an order ID and the partner fee.
 * @param {string} orderId - The ID of the order to complete.
 * @param {number} partnerFee - The partner fee to apply to the transaction.
 * @returns {Promise} A promise that resolves with the PayPal response if successful, and rejects with an error if not.
 */
export const autoCompleteOrder = async (orderId, partnerFee) => {
  try {
    const response = await PayPalApi.autoCompleteOrder({ orderId, partnerFee });
    return response;
  } catch (err) {
    const e = storableError(err);
    log.error(err, 'auto-complete-paypal-order-failed', e);
    throw e;
  }
};
